import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    isChunkError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      /Loading chunk [0-9]+ failed/i.test(error.message) ||
      /Loading CSS chunk [0-9]+ failed/i.test(error.message);

    return { hasError: true, error, isChunkError };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in React component tree:', error, errorInfo);

    // If it's a chunk loading mismatch after a deployment, attempt auto-refresh once
    if (this.state.isChunkError) {
      const reloadKey = 'app_chunk_err_reload_ts';
      const lastReload = sessionStorage.getItem(reloadKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        sessionStorage.setItem(reloadKey, String(now));
        window.location.reload();
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      const isChunk = this.state.isChunkError;

      return (
        <div className="min-h-screen flex items-center justify-center bg-whiten dark:bg-boxdark-2 p-6">
          <div className="max-w-md w-full rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-8 shadow-card text-center">
            <div
              className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 border ${
                isChunk
                  ? 'bg-brand-blue-tint text-brand-vib border-brand-vib/20 dark:bg-brand-900/40 dark:text-brand-300'
                  : 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 border-rose-200 dark:border-rose-800'
              }`}
            >
              {isChunk ? (
                <svg className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-6 h-6 fill-current" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>

            <h2 className="text-lg font-bold text-brand-navy dark:text-white mb-2">
              {isChunk ? 'New Application Version Available' : 'Something went wrong loading this view'}
            </h2>

            <p className="text-xs font-mono text-brand-muted dark:text-bodydark mb-6">
              {isChunk
                ? 'A new build has been deployed. Reload to get the latest updates.'
                : this.state.error?.message || 'An unexpected rendering error occurred.'}
            </p>

            <button
              type="button"
              onClick={() => {
                this.setState({ hasError: false, error: null, isChunkError: false });
                window.location.reload();
              }}
              className="w-full py-2.5 px-4 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm cursor-pointer"
            >
              {isChunk ? 'Update & Reload' : 'Reload Application'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
